import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Calendar,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Building2,
  Pencil,
  Store,
  Users,
  KeyRound,
  FolderKanban,
  Radio,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
// PRD §20 — el calendario de la marca, incrustado. Es el MISMO que el del
// menú: antes había aquí un segundo esbozo con su propio mes fijo y sus
// propios fallos, y dos calendarios que se contradicen son peor que uno.
import { CalendarPage } from '@/features/calendar/CalendarPage';
// PRD §7.6 — two-layer privacy (profile / activity).
import { ProfilePrivacySection } from './ProfilePrivacySection';
import { useAuth } from '@/lib/auth/useAuth';
import { authApi } from '@/features/auth/api';
import { useAuthStore } from '@/lib/auth/authStore';
import { useCurrentCompany } from '@/lib/branding/useCurrentBrand';
import type { CoCompany, User } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Collapsible } from '@/components/ui/Collapsible';
import { toast } from '@/components/ui/Toast';
import { Pagination } from '@/components/ui/Pagination';
import { LANGUAGES, THEMES, usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { EditProfileDialog, type EditProfileInput } from './EditProfileDialog';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { ACTIVITY_PER_PAGE, profileActivityApi } from './activityApi';
import type {
  ActivityHistoryEntry,
  ActivityHistoryKind,
  ActivityResource,
  ActivityResourceType,
} from './activityTypes';

const DEFAULT_BANNER =
  'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&w=1400&q=80';

type ProfileMe = User & { fullName: string };

/**
 * The backend returns `role` as an object `{id, name}` (joined from the roles
 * table); some legacy paths return a string. Render the name either way and
 * fall back to nothing rather than blowing up if the shape changes again.
 */
function roleLabel(role: ProfileMe['role']): string | null {
  if (!role) return null;
  if (typeof role === 'string') return role;
  return role.name ?? null;
}

type TabId = 'events' | 'brands' | 'projects' | 'stations' | 'team';

const TAB_IDS: TabId[] = ['events', 'brands', 'projects', 'stations', 'team'];
const TAB_LABEL_KEYS: Record<TabId, string> = {
  events: 'profile.tabs.events',
  brands: 'profile.tabs.brands',
  projects: 'profile.tabs.projects',
  stations: 'profile.tabs.stations',
  team: 'profile.tabs.team',
};

/** The four resource tabs map onto an activity resource type. `team` does not. */
const TAB_RESOURCE_TYPE: Partial<Record<TabId, ActivityResourceType>> = {
  events: 'event',
  brands: 'brand',
  projects: 'project',
  stations: 'station',
};

const RESOURCE_ICON: Record<ActivityResourceType, LucideIcon> = {
  brand: Store,
  event: Calendar,
  project: FolderKanban,
  station: Radio,
};

export function ProfilePage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const patchAuthUser = useAuthStore((s) => s.patchUser);
  const t = useT();
  useFavoritable('Profile');
  const [tab, setTab] = useState<TabId>('events');
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  // Count badge for the active resource tab — reported up by ActivityTab.
  const [tabCount, setTabCount] = useState<number | null>(null);

  const profileQ = useQuery({
    queryKey: queryKeys.profile.me,
    queryFn: () => api.get<ProfileMe>('/profile'),
  });
  const { company } = useCurrentCompany();

  const save = useMutation({
    // Backend exposes PUT /profile (full update with optional fields); there is
    // no PATCH route. See ox-platform/.../profile.controller.ts.
    mutationFn: (patch: EditProfileInput) => api.put<ProfileMe>('/profile', patch),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.profile.me, next);
      patchAuthUser(next);
      setEditOpen(false);
      toast({ tone: 'success', title: 'Profile updated' });
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: 'Could not update profile', description: e.message }),
  });

  const profile = profileQ.data ?? null;
  const loading = profileQ.isLoading;

  if (loading || !profile) {
    return (
      <div className="page">
        <Skeleton className="mb-6 h-56 rounded-2xl" />
        <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
          <Skeleton className="h-96 rounded-2xl" />
          <Skeleton className="h-96 rounded-2xl" />
        </div>
      </div>
    );
  }

  const resourceType = TAB_RESOURCE_TYPE[tab];

  return (
    <div className="pb-12">
      {/* ── Banner + avatar + tabs ───────────────────────────── */}
      <ProfileHeader
        profile={profile}
        tabs={TAB_IDS.map((id) => ({
          id,
          label: t(TAB_LABEL_KEYS[id]),
          count: id === tab && id !== 'team' ? tabCount ?? undefined : undefined,
        }))}
        activeTab={tab}
        onTabChange={(id) => {
          setTab(id as TabId);
          setTabCount(null);
        }}
      />

      {/* ── Body grid ────────────────────────────────────────── */}
      <div className="grid gap-8 px-4 pt-12 sm:px-6 lg:grid-cols-[minmax(280px,1fr)_3fr] lg:px-8">
        <ProfileSidebar
          profile={profile}
          company={company}
          onEdit={() => setEditOpen(true)}
          onChangePassword={() => setPasswordOpen(true)}
          onSignOut={async () => {
            await authApi.logout().catch(() => undefined);
            logout();
            navigate('/login');
          }}
        />
        {/* On mobile the grid stacks to one column: pull the right column
            above the sidebar so the Calendar + resource list sit directly
            under the tab bar that controls them. On lg the two-column
            layout resets (sidebar left, calendar + resource list right). */}
        <div className="order-first space-y-6 lg:order-none">
          {/* PRD §20 — Calendar v1 sits above the per-tab resource list so
              "what's on my plate this week" is the first thing the user
              sees on the profile page, beside the My Experience sidebar. */}
          <CalendarPage embedded />
          {/* TabBar moved here from the header so it sits directly above
              the resource cards it controls. Calendar is intentionally
              ABOVE this — it's not tab-scoped. */}
          <TabBar
            tabs={TAB_IDS.map((id) => ({
              id,
              label: t(TAB_LABEL_KEYS[id]),
              count:
                id === tab && id !== 'team' ? tabCount ?? undefined : undefined,
            }))}
            activeTab={tab}
            onTabChange={(id) => {
              setTab(id as TabId);
              setTabCount(null);
            }}
          />
          {resourceType ? (
            <ActivityTab key={resourceType} type={resourceType} onTotal={setTabCount} />
          ) : (
            <EmptyTab
              icon={<Users className="h-9 w-9" strokeWidth={1.5} />}
              title={t('profile.team.emptyTitle')}
              subtitle={t('profile.team.emptySubtitle')}
            />
          )}
        </div>
      </div>

      <EditProfileDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        profile={profile}
        loading={save.isPending}
        onSave={(v) => save.mutate(v)}
      />

      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </div>
  );
}

// ── Header ─────────────────────────────────────────────────────────────────

function ProfileHeader({
  profile,
  // The tabs were moved out of the header to live above the resource
  // cards in the body. The props remain so callers don't churn; ignore
  // them here.
  tabs: _tabs,
  activeTab: _activeTab,
  onTabChange: _onTabChange,
}: {
  profile: ProfileMe;
  tabs: { id: string; label: string; count?: number }[];
  activeTab: string;
  onTabChange: (id: string) => void;
}) {
  const cover = profile.coverPicture || DEFAULT_BANNER;
  const handle = profile.username || profile.email.split('@')[0];
  const initials = `${profile.firstname?.[0] ?? ''}${profile.lastname?.[0] ?? ''}`
    .trim()
    .toUpperCase() || profile.email[0].toUpperCase();
  return (
    <div className="relative">
      {/* Banner */}
      <div
        className="relative h-56 w-full overflow-hidden rounded-b-2xl bg-cover bg-center"
        style={{ backgroundImage: `url(${resolveImageUrl(cover)})` }}
      >
        <div className="absolute inset-0 bg-[rgb(0_0_0/0.45)]" />
        {(() => {
          // Render the title (free-form) if set; otherwise fall back to the
          // role name. profile.role can be `{id, name}` from the backend or a
          // plain string from the auth response — roleLabel() normalises both.
          const badge = profile.title || roleLabel(profile.role);
          return badge ? (
            <span
              className="absolute bottom-4 right-6 rounded-md bg-[var(--color-xk-accent)] px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider text-[var(--color-fg-on-accent)] shadow-md"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {badge}
            </span>
          ) : null;
        })()}
      </div>

      {/* Info bar */}
      <div className="relative -mt-10 flex flex-wrap items-end justify-between gap-4 px-4 sm:px-6 lg:px-8">
        {/* Avatar */}
        <div className="absolute -top-12 left-4 sm:left-6 lg:left-8">
          <div className="rounded-full bg-gradient-to-b from-transparent to-[var(--color-xk-accent)] p-1">
            {profile.picture ? (
              <img
                src={resolveImageUrl(profile.picture)}
                alt={profile.fullName}
                className="h-32 w-32 rounded-full border-4 border-[var(--color-xk-bg)] bg-[var(--color-xk-bg)] object-cover"
              />
            ) : (
              <div
                className="grid h-32 w-32 place-items-center rounded-full border-4 border-[var(--color-xk-bg)] bg-[var(--color-xk-accent)] text-3xl font-black text-[var(--color-fg-on-accent)]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {initials}
              </div>
            )}
          </div>
        </div>

        {/* Name */}
        <div className="ml-44 mt-16">
          <h1
            className="text-[28px] font-black tracking-tight text-[var(--color-xk-lavender)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {profile.fullName}
          </h1>
          <p className="mt-1 text-[14px] font-semibold text-[var(--color-accent-ink)]">
            @{handle}
          </p>
        </div>

        {/* Tabs moved out of the header — now render in the body's right
            column so they sit above the resource cards they control,
            while the Calendar lives ABOVE the tabs as the new "what's
            on my plate" hero. */}
      </div>
    </div>
  );
}

function TabBar({
  tabs,
  activeTab,
  onTabChange,
}: {
  tabs: { id: string; label: string; count?: number }[];
  activeTab: string;
  onTabChange: (id: string) => void;
}) {
  return (
    <div
      className="mt-16 flex max-w-full items-center gap-1 overflow-x-auto overflow-y-hidden rounded-xl border border-[var(--color-xk-border)] bg-[rgb(0_0_0/0.3)] p-1 backdrop-blur scrollbar-thin"
    >
      {tabs.map((t) => {
        const active = t.id === activeTab;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onTabChange(t.id)}
            className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-[13px] font-bold transition-all ${
              active
                ? 'bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)] shadow-md'
                : 'text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]'
            }`}
          >
            {t.label}
            {typeof t.count === 'number' && (
              <span
                className={`rounded-full px-1.5 py-px text-[11px] font-extrabold ${
                  active
                    ? 'bg-[rgb(0_0_0/0.2)] text-[var(--color-fg-on-accent)]'
                    : 'bg-[var(--color-xk-surface)] text-[var(--color-xk-text-muted)]'
                }`}
              >
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────

function ProfileSidebar({
  profile,
  company,
  onEdit,
  onChangePassword,
  onSignOut,
}: {
  profile: ProfileMe;
  company: CoCompany | null;
  onEdit: () => void;
  onChangePassword: () => void;
  onSignOut: () => void;
}) {
  const t = useT();
  const hasProfessional = profile.title || company?.name || profile.studiedAt;
  const hasTags = (profile.languages?.length ?? 0) > 0 || (profile.interests?.length ?? 0) > 0;
  return (
    <aside className="h-fit rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-6 backdrop-blur">
      <h2
        className="mb-4 text-[18px] font-black text-[var(--color-xk-lavender)]"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {t('profile.sidebar.experience')}
      </h2>
      <p className="mb-6 text-[13px] leading-relaxed text-[var(--color-xk-text-muted)]">
        {profile.bio || t('profile.sidebar.noBio')}
      </p>

      <SidebarSection title={t('profile.sidebar.contact')}>
        <InfoRow icon={<Mail className="h-3.5 w-3.5" />} value={profile.email} />
        {profile.phone && <InfoRow icon={<Phone className="h-3.5 w-3.5" />} value={profile.phone} />}
        {profile.location && (
          <InfoRow icon={<MapPin className="h-3.5 w-3.5" />} value={profile.location} />
        )}
      </SidebarSection>

      {hasProfessional && (
        <SidebarSection title={t('profile.sidebar.professional')}>
          {profile.title && (
            <InfoRow icon={<Briefcase className="h-3.5 w-3.5" />} value={profile.title} />
          )}
          {company?.name && (
            <InfoRow icon={<Building2 className="h-3.5 w-3.5" />} value={company.name} />
          )}
          {profile.studiedAt && (
            <InfoRow icon={<GraduationCap className="h-3.5 w-3.5" />} value={profile.studiedAt} />
          )}
        </SidebarSection>
      )}

      {hasTags && (
        <SidebarSection title={t('profile.sidebar.additional')}>
          {(profile.languages?.length ?? 0) > 0 && (
            <TagGroup label={t('profile.sidebar.languages')} tags={profile.languages!} />
          )}
          {(profile.interests?.length ?? 0) > 0 && (
            <TagGroup label={t('profile.sidebar.interests')} tags={profile.interests!} />
          )}
        </SidebarSection>
      )}

      <SidebarSection title={t('profile.sidebar.preferences')}>
        <PreferencesPicker />
      </SidebarSection>

      <button
        type="button"
        onClick={onEdit}
        className="mt-8 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] px-4 py-2.5 text-[13px] font-bold text-[var(--color-xk-lavender)] transition-colors hover:bg-[var(--color-xk-surface-hover)]"
      >
        <Pencil className="h-3.5 w-3.5" />
        {t('profile.sidebar.editProfile')}
      </button>
      <button
        type="button"
        onClick={onChangePassword}
        className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] px-4 py-2.5 text-[13px] font-bold text-[var(--color-xk-lavender)] transition-colors hover:bg-[var(--color-xk-surface-hover)]"
      >
        <KeyRound className="h-3.5 w-3.5" />
        {t('profile.sidebar.changePassword')}
      </button>

      {/* PRD §7.6 — two-layer privacy model. Defaults private; toggles
          persist via PUT /profile. */}
      <ProfilePrivacySection
        initial={{
          profilePublic: (profile as any).profilePublic ?? false,
          activityPublic: (profile as any).activityPublic ?? false,
        }}
      />

      <Button
        variant="ghost"
        className="mt-2 w-full justify-center text-[12px] text-[var(--color-xk-text-muted)]"
        onClick={onSignOut}
      >
        {t('common.signOut')}
      </Button>
    </aside>
  );
}

function SidebarSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6 last:mb-0">
      <h3
        className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[var(--color-accent-ink)]"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

function InfoRow({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <div className="flex items-center gap-3 py-1.5 text-[13px] text-[var(--color-xk-lavender)]">
      <span className="shrink-0 text-[var(--color-xk-text-muted)]">{icon}</span>
      <span className="truncate">{value}</span>
    </div>
  );
}

function TagGroup({ label, tags }: { label: string; tags: string[] }) {
  return (
    <div className="mt-3 first:mt-0">
      <span className="mb-2 block text-[11px] text-[var(--color-xk-text-muted)]">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t, i) => (
          <span
            key={`${t}-${i}`}
            className="rounded-md bg-[var(--color-xk-accent)] px-2.5 py-1 text-[11px] font-extrabold text-[var(--color-fg-on-accent)]"
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Theme + language picker that mirrors the TopBar's compact menu, but laid
 * out as inline swatches/buttons so the user can scan and pick in-place from
 * the profile sidebar. Both bind to the same `usePrefsStore`, so a change
 * here propagates to the TopBar instantly.
 */
function PreferencesPicker() {
  const t = useT();
  const theme = usePrefsStore((s) => s.theme);
  const setTheme = usePrefsStore((s) => s.setTheme);
  const language = usePrefsStore((s) => s.language);
  const setLanguage = usePrefsStore((s) => s.setLanguage);
  return (
    <div className="space-y-4">
      <div>
        <span className="mb-2 block text-[11px] text-[var(--color-xk-text-muted)]">{t('profile.sidebar.theme')}</span>
        <div className="grid grid-cols-3 gap-1.5">
          {THEMES.map((th) => {
            const isActive = th.id === theme;
            return (
              <button
                key={th.id}
                type="button"
                onClick={() => setTheme(th.id)}
                title={`${th.label} · ${th.mode}`}
                className={
                  isActive
                    ? 'flex flex-col items-stretch overflow-hidden rounded-md border-2 border-[var(--color-accent-ink)]'
                    : 'flex flex-col items-stretch overflow-hidden rounded-md border border-[var(--color-xk-border)] transition-colors hover:border-[var(--color-xk-border-strong)]'
                }
              >
                <span aria-hidden="true" className="flex h-7">
                  <span className="flex-1" style={{ backgroundColor: th.preview.bg }} />
                  <span className="flex-1" style={{ backgroundColor: th.preview.surface }} />
                  <span className="w-3 flex-none" style={{ backgroundColor: th.preview.accent }} />
                </span>
                <span className="px-1.5 py-1 text-[10px] font-bold text-[var(--color-xk-lavender)]">
                  {th.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span className="mb-2 block text-[11px] text-[var(--color-xk-text-muted)]">{t('profile.sidebar.language')}</span>
        <div className="flex gap-1.5">
          {LANGUAGES.map((l) => {
            const isActive = l.id === language;
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => setLanguage(l.id)}
                className={
                  isActive
                    ? 'flex-1 rounded-md border-2 border-[var(--color-accent-ink)] bg-[var(--color-accent-08)] px-2 py-1.5 text-[12px] font-bold text-[var(--color-accent-ink)]'
                    : 'flex-1 rounded-md border border-[var(--color-xk-border)] px-2 py-1.5 text-[12px] font-bold text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-xk-border-strong)]'
                }
              >
                <span className="mr-1.5 text-[10px] font-extrabold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
                  {l.flag}
                </span>
                {l.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Activity tab ────────────────────────────────────────────────────────────

/**
 * One resource tab (Brands / Events / Projects / Stations). Shows the
 * resources the logged-in user has personally created, edited or deleted,
 * server-paginated, each card carrying its own collapsible write-history.
 */
function ActivityTab({
  type,
  onTotal,
}: {
  type: ActivityResourceType;
  onTotal: (total: number) => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const q = useQuery({
    queryKey: queryKeys.profile.activity(type, page),
    queryFn: () => profileActivityApi.list(type, page),
    placeholderData: (prev) => prev,
  });

  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;

  useEffect(() => {
    if (q.data) onTotal(total);
  }, [q.data, total, onTotal]);

  if (q.isLoading) {
    return (
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-40 rounded-2xl" />
        ))}
      </div>
    );
  }

  if (q.isError) {
    return (
      <EmptyTab
        title={t('profile.activity.loadErrorTitle')}
        subtitle={t('profile.activity.loadErrorSubtitle')}
      />
    );
  }

  if (items.length === 0) {
    const EmptyIcon = RESOURCE_ICON[type];
    return (
      <EmptyTab
        icon={<EmptyIcon className="h-9 w-9" strokeWidth={1.5} />}
        title={t('profile.activity.emptyTitle')}
        subtitle={t('profile.activity.emptySubtitle')}
      />
    );
  }

  const pageCount = Math.max(1, Math.ceil(total / ACTIVITY_PER_PAGE));
  const pager = {
    page,
    pageCount,
    total,
    from: (page - 1) * ACTIVITY_PER_PAGE + 1,
    to: Math.min(page * ACTIVITY_PER_PAGE, total),
    setPage,
    next: () => setPage((p) => Math.min(pageCount, p + 1)),
    prev: () => setPage((p) => Math.max(1, p - 1)),
  };

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((resource) => (
          <ActivityResourceCard
            key={resource.resourceId}
            resource={resource}
            onOpen={resolveRoute(resource, navigate)}
          />
        ))}
      </div>
      <Pagination {...pager} noun={t('profile.activity.noun')} />
    </div>
  );
}

/** Navigation handler for a card, or `undefined` when the resource is gone. */
function resolveRoute(
  resource: ActivityResource,
  navigate: ReturnType<typeof useNavigate>,
): (() => void) | undefined {
  if (resource.deleted) return undefined;
  const { resourceType, resourceId, parentId } = resource;
  let path: string | null = null;
  if (resourceType === 'brand') path = `/brand/${resourceId}`;
  else if (resourceType === 'project') path = `/projects/${resourceId}`;
  else if (resourceType === 'station') path = `/stations/${resourceId}`;
  else if (resourceType === 'event' && parentId != null)
    path = `/projects/${parentId}/events/${resourceId}`;
  if (!path) return undefined;
  const target = path;
  return () => navigate(target);
}

function ActivityResourceCard({
  resource,
  onOpen,
}: {
  resource: ActivityResource;
  onOpen?: () => void;
}) {
  const t = useT();
  const [historyOpen, setHistoryOpen] = useState(false);
  const Icon = RESOURCE_ICON[resource.resourceType];
  const clickable = Boolean(onOpen);

  const header = (
    <>
      <span className="grid h-9 w-9 flex-none place-items-center rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] text-[var(--color-accent-ink)]">
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <div className="min-w-0 flex-1">
        <h4
          className="truncate text-[15px] font-extrabold text-[var(--color-xk-lavender)]"
          title={resource.name}
        >
          {resource.name}
        </h4>
        <p className="truncate text-[12px] font-semibold text-[var(--color-xk-text-muted)]">
          {t('profile.activity.lastActivity', {
            date: formatDate(resource.lastActionAt),
          })}
        </p>
      </div>
      {resource.deleted && (
        <Badge tone="error" className="flex-none">
          {t('profile.activity.badgeDeleted')}
        </Badge>
      )}
    </>
  );

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)]',
        resource.deleted && 'opacity-60',
      )}
    >
      <div className="h-[3px] w-full bg-[var(--color-xk-accent)]" />
      <div className="flex flex-1 flex-col px-5 py-4">
        {clickable ? (
          <button
            type="button"
            onClick={onOpen}
            className="flex items-start gap-2.5 rounded-md text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-xk-accent)]"
          >
            {header}
          </button>
        ) : (
          <div className="flex items-start gap-2.5">{header}</div>
        )}

        <div className="mt-3 border-t border-[var(--color-xk-border)] pt-3">
          <Collapsible
            open={historyOpen}
            onOpenChange={setHistoryOpen}
            label={t('profile.activity.historyToggle', {
              count: resource.history.length,
            })}
          >
            <ul className="space-y-2.5">
              {resource.history.map((entry, i) => (
                <ActivityHistoryRow
                  key={i}
                  entry={entry}
                  type={resource.resourceType}
                />
              ))}
            </ul>
          </Collapsible>
        </div>
      </div>
    </div>
  );
}

const HISTORY_TONE: Record<ActivityHistoryKind, string> = {
  created: 'var(--color-success)',
  updated: 'var(--color-warn)',
  deleted: '#EF4444',
  restored: '#3B82F6',
};

function ActivityHistoryRow({
  entry,
  type,
}: {
  entry: ActivityHistoryEntry;
  type: ActivityResourceType;
}) {
  const t = useT();
  const typeNoun = t(`profile.activity.type.${type}`);
  let text: string;
  if (entry.kind === 'updated') {
    text = entry.changedFields.length
      ? t('profile.activity.history.updated', {
          fields: entry.changedFields.join(', '),
        })
      : t('profile.activity.history.updatedGeneric', { type: typeNoun });
  } else {
    text = t(`profile.activity.history.${entry.kind}`, { type: typeNoun });
  }
  return (
    <li className="flex items-start gap-2.5">
      <span
        aria-hidden="true"
        className="mt-1.5 h-2 w-2 flex-none rounded-full"
        style={{ backgroundColor: HISTORY_TONE[entry.kind] }}
      />
      <div className="min-w-0">
        <p className="text-[12px] font-semibold text-[var(--color-xk-lavender)]">
          {text}
        </p>
        <p className="text-[11px] text-[var(--color-xk-text-muted)]">
          {formatDateTime(entry.createdAt)}
        </p>
      </div>
    </li>
  );
}

function formatDate(value: string): string {
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY') : value;
}

function formatDateTime(value: string): string {
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY · HH:mm') : value;
}

function EmptyTab({
  icon,
  title,
  subtitle,
}: {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-6 py-16 text-center">
      {icon && (
        <div className="mb-4 inline-flex text-[var(--color-xk-text-muted)] opacity-70">
          {icon}
        </div>
      )}
      <h3
        className="mb-1.5 text-[16px] font-black text-[var(--color-xk-lavender)]"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {title}
      </h3>
      {subtitle && <p className="text-[13px] text-[var(--color-xk-text-muted)]">{subtitle}</p>}
    </div>
  );
}
